import { useMemo } from "react";
import { getTerpeneByShortName } from "../lib/terpenesData";
import { TERPENES_LIST } from "../lib/strainsData";
import { useLanguage } from "../lib/LanguageContext";
import { translateTerpeneName } from "../lib/dataTranslations";

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Renders a difference-focused terpene comparison.
 * Terpenes are sorted by the spread (max - min) across strains, so the
 * biggest differences surface at the top. Each row shows, per strain,
 * a horizontal bar and the percentage, with the dominant strain highlighted.
 */
export default function TerpeneDifferenceView({ strains, colors }) {
  const { language } = useLanguage();

  const rows = useMemo(() => {
    const active = TERPENES_LIST.filter((tp) =>
      strains.some((s) => (s.terpenes[tp] || 0) > 0)
    );
    return active
      .map((tp) => {
        const values = strains.map((s, i) => ({
          strain: s,
          color: colors[i],
          value: s.terpenes[tp] || 0,
        }));
        const max = Math.max(...values.map((v) => v.value));
        const min = Math.min(...values.map((v) => v.value));
        const spread = max - min;
        const dominant = values.reduce((m, v) => (v.value > m.value ? v : m), values[0]);
        return {
          tp,
          label: translateTerpeneName(capitalize(tp), language),
          terpene: getTerpeneByShortName(tp),
          values,
          max,
          spread,
          dominant,
        };
      })
      .sort((a, b) => b.spread - a.spread);
  }, [strains, colors, language]);

  if (rows.length === 0) return null;

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.tp} className="rounded-lg border border-border bg-background/50 p-3">
          {/* Terpene label + emoji */}
          <div className="flex items-center gap-2 mb-2">
            {row.terpene?.image ? (
              <img
                src={row.terpene.image}
                alt={row.label}
                className="w-4 h-4 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              row.terpene?.emoji
            )}
            <span className="text-xs font-semibold">{row.label}</span>
            {row.spread > 0 && (
              <span className="text-[10px] text-muted-foreground ml-auto">
                {language === "pl" ? "różnica" : "diff"} {row.spread}%
              </span>
            )}
          </div>
          {/* Per-strain bars */}
          <div className="space-y-1.5">
            {row.values.map((v) => {
              const isDominant = row.spread > 0 && v.value === row.max && v.value > 0;
              const width = row.max > 0 ? (v.value / row.max) * 100 : 0;
              return (
                <div key={v.strain.id} className="flex items-center gap-2">
                  <span className="text-[11px] truncate w-20 sm:w-24" style={{ color: v.color }}>
                    {v.strain.name}
                  </span>
                  <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${width}%`,
                        backgroundColor: v.color,
                        opacity: isDominant ? 1 : 0.45,
                      }}
                    />
                  </div>
                  <span
                    className={`text-[11px] tabular-nums w-9 text-right ${
                      isDominant ? "font-bold" : "text-muted-foreground"
                    }`}
                    style={isDominant ? { color: v.color } : {}}
                  >
                    {v.value}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}